import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * OfferOverviewComponent displays and edits the basic offer information (name, promo code, description) for a campaign, providing validation and emitting changes.
 * @component
 * @selector app-offer-overview
 * @example
 *   <app-offer-overview
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)">
 *   </app-offer-overview>
 * @input {boolean} isExpanded - Controls whether the overview section is expanded or collapsed (default: true).
 * @input {any} campaignData - Initial campaign payload used to populate offerName, promoCode, and offerDescription.
 * @input {boolean} isCompleted - Indicates whether the step is marked complete (affects UI/flow).
 * @input {boolean} isSaving - When true, Done shows a spinner and is disabled while persist is in flight.
 * @input {string} promoCodeConflictMessage - Parent-persisted promo conflict so the inline error survives tab recreation.
 * @output {EventEmitter<boolean>} toggle - Emitted when the section is expanded or collapsed with the new expanded state.
 * @output {EventEmitter<void>} done - Emitted when the user attempts to finish the overview step and validation passes.
 * @output {EventEmitter<any>} dataChange - Emitted whenever any form field changes; payload contains { offer_name, promo_code, offer_description }.
 * @method onOfferOverviewToggle(expanded: boolean) - Handles toggle action and emits the toggle event.
 * @method onOfferOverviewDone() - Validates all fields and emits done if the form is valid.
 * @method onFormChange(fieldName: string) - Handles typing in a field, enforces length checks, clears errors, emits dataChange, and re-evaluates validity.
 * @method validateField(fieldName: string) - Validates a single field, updates validationErrors for required and length rules, and updates overall validity.
 * @method validateAllFields() - Marks all fields as touched and runs validation for each required field.
 * @method onFieldFocus(fieldName: string) - Placeholder for focus behavior or styling when a field gains focus.
 * @method onFieldBlur(fieldName: string) - Marks a field as touched and triggers validation for that field.
 * @method getFieldLabel(fieldName: string): string - Returns a human-readable label for a given field key.
 * @method checkFormValidity() - Calculates isFormValid based on presence of values, touched/validation states, and character limits.
 * @method hasError(fieldName: string): boolean - Returns true when a field has been touched and contains a validation error.
 * @lifecycle ngOnInit - Initializes form fields from campaignData if provided on component initialization.
 * @lifecycle ngOnChanges - Updates internal form fields whenever campaignData input changes.
 */
@Component({
  selector: 'app-offer-overview',
  imports: [CollapsibleSectionComponent, FormsModule, CommonModule],
  templateUrl: './offer-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-overview.component.scss',
})
export class OfferOverviewComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Input() isSaving = false;
  @Input() promoCodeConflictMessage = '';
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  offerName = '';
  promoCode = '';
  offerDescription = '';

  // Validation properties
  validationErrors: any = {
    offerName: '',
    promoCode: '',
    offerDescription: '',
  };

  fieldTouched: any = {
    offerName: false,
    promoCode: false,
    offerDescription: false,
  };

  isFormValid = false;

  ngOnInit(): void {
    this.syncFromCampaignData();
    this.applyPromoCodeConflict();
  }

  ngOnChanges(): void {
    this.syncFromCampaignData();
    this.applyPromoCodeConflict();
  }

  private applyPromoCodeConflict(): void {
    if (!this.promoCodeConflictMessage) {
      return;
    }
    this.handleApiError({ message: this.promoCodeConflictMessage });
  }

  private syncFromCampaignData(): void {
    if (this.campaignData) {
      this.offerName = this.campaignData.offer_name || '';
      this.promoCode = this.campaignData.promo_code || '';
      this.offerDescription = this.campaignData.offer_description || '';
    }
  }

  onOfferOverviewToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onOfferOverviewDone(): void {
    if (this.isSaving) {
      return;
    }

    this.validateAllFields();

    if (this.isFormValid) {
      this.done.emit();
    }
  }

  /**
   * Handle incremental changes to a form field, update validation errors if needed, emit the updated offer data, and re-evaluate form validity.
   * @example
   * nFormChange('promoCode')
   * undefined
   * @param {{string}} {{fieldName}} - The name of the form field that changed.
   * @returns {{void}} No return value.
   **/
  onFormChange(fieldName: string): void {
    // Validate field length if it exceeds limit
    const value = this[fieldName as keyof this] as string;
    if (fieldName === 'promoCode' && value && value.length > 100) {
      this.validationErrors[fieldName] = 'Promo Code cannot exceed 100 characters';
    } else if (this.validationErrors[fieldName]) {
      // Clear the error message for the field being typed into
      this.validationErrors[fieldName] = '';
    }

    const data = {
      offer_name: this.offerName,
      promo_code: this.promoCode,
      offer_description: this.offerDescription,
    };
    this.dataChange.emit(data);
    this.checkFormValidity(); // Re-evaluate form validity after clearing an error
  }

  // Validation methods
  /**
   * Validate a single named field, set appropriate validation error messages, and update form validity.
   * @example
   * validateField('offerName')
   * undefined
   * @param {{string}} {{fieldName}} - Field name to validate.
   * @returns {{void}} Return nothing; updates this.validationErrors and calls this.checkFormValidity().
   **/
  validateField(fieldName: string): void {
    const value = this[fieldName as keyof this] as string;

    if (!value || value.trim() === '') {
      this.validationErrors[fieldName] = `${this.getFieldLabel(fieldName)} is required`;
    } else {
      // Check character limits
      if (fieldName === 'offerName' && value.length > 100) {
        this.validationErrors[fieldName] = 'Offer Name cannot exceed 100 characters';
      } else if (fieldName === 'promoCode' && value.length > 100) {
        this.validationErrors[fieldName] = 'Promo Code cannot exceed 100 characters';
      } else if (fieldName === 'offerDescription' && value.length > 500) {
        this.validationErrors[fieldName] = 'Offer Description cannot exceed 500 characters';
      } else {
        this.validationErrors[fieldName] = '';
      }
    }

    this.checkFormValidity();
  }

  validateAllFields(): void {
    this.fieldTouched.offerName = true;
    this.fieldTouched.promoCode = true;
    this.fieldTouched.offerDescription = true;

    this.validateField('offerName');
    this.validateField('promoCode');
    this.validateField('offerDescription');
  }

  onFieldFocus(fieldName: string): void {
    // Optional: Add focus styling or behavior
  }

  onFieldBlur(fieldName: string): void {
    this.fieldTouched[fieldName] = true;
    this.validateField(fieldName);
  }

  getFieldLabel(fieldName: string): string {
    const labels: any = {
      offerName: 'Offer Name',
      promoCode: 'Promo Code',
      offerDescription: 'Offer Description',
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * Surface API validation errors from the parent (e.g. promo code already in use).
   */
  handleApiError(error: any): void {
    if (error?.message?.includes('Promo code already in use')) {
      this.validationErrors.promoCode = error.message;
      this.fieldTouched.promoCode = true;
      this.checkFormValidity();
    }
  }

  checkFormValidity(): void {
    this.isFormValid =
      !this.validationErrors.offerName &&
      !this.validationErrors.promoCode &&
      !this.validationErrors.offerDescription &&
      this.offerName.trim() !== '' &&
      this.promoCode.trim() !== '' &&
      this.offerDescription.trim() !== '' &&
      this.offerName.length <= 100 &&
      this.promoCode.length <= 100 &&
      this.offerDescription.length <= 500;
  }

  hasError(fieldName: string): boolean {
    return this.fieldTouched[fieldName] && Boolean(this.validationErrors[fieldName]);
  }
}
